import React, { useEffect, useState } from "react";
import { Plus, Trash2, Pencil } from "lucide-react";
import { AppLayout, PageHeader } from "@/components/AppLayout";
import { DataTable, Toolbar, SearchInput, Btn, Field, Input, Select, Textarea, Modal } from "@/components/Primitives";
import { api, formatApiErrorDetail, ORIGINS, can } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const TABS = ["Customers", "Suppliers", "Products", "Paint Colours", "Export Destinations"];

export default function MasterData() {
  const { user } = useAuth();
  const [tab, setTab] = useState("Customers");

  return (
    <AppLayout>
      <PageHeader title="Master Data" subtitle="Customers, suppliers, products, paint colours and destinations." />
      <div className="flex border-b border-stone-200 mb-4">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium ${tab === t ? "border-b-2 border-emerald-800 text-emerald-900" : "text-stone-500 hover:text-stone-800"}`}
            data-testid={`tab-${t.toLowerCase().replace(/\s+/g, "-")}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "Customers" && <CustomersTab role={user?.role} />}
      {tab === "Suppliers" && <SuppliersTab role={user?.role} />}
      {tab === "Products" && <ProductsTab role={user?.role} />}
      {tab === "Paint Colours" && <PaintColoursTab role={user?.role} />}
      {tab === "Export Destinations" && <ExportDestinationsTab role={user?.role} />}
    </AppLayout>
  );
}

function CustomersTab({ role }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const blank = { name: "", code: "", country: "", address: "", contact_person: "", email: "", phone: "", currency: "ZAR", vat_number: "" };
  const [form, setForm] = useState(blank);
  const writable = can(role, "master:write");
  const load = async () => setItems((await api.get("/customers")).data);
  useEffect(() => { load(); }, []);
  const openNew = () => { setEditingId(null); setForm(blank); setOpen(true); };
  const openEdit = (row) => { setEditingId(row.id); setForm({ ...blank, ...row }); setOpen(true); };
  const save = async () => {
    if (!form.name?.trim()) { toast.error("Name is required"); return; }
    try {
      if (editingId) { await api.patch(`/customers/${editingId}`, form); toast.success("Customer updated"); }
      else { await api.post("/customers", form); toast.success("Customer added"); }
      setOpen(false); setEditingId(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const remove = async (row) => {
    if (!window.confirm(`Delete customer "${row.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/customers/${row.id}`); toast.success("Customer deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  return (
    <>
      <Toolbar>
        {writable && <Btn onClick={openNew} testid="new-customer-btn"><Plus className="h-4 w-4" /> New Customer</Btn>}
        <div className="ml-auto text-xs text-stone-500">{items.length} customers</div>
      </Toolbar>
      <DataTable
        columns={[
          { header: "Name", key: "name" }, { header: "Code", key: "code" }, { header: "Country", key: "country" },
          { header: "Contact", key: "contact_person" }, { header: "Email", key: "email" }, { header: "VAT", key: "vat_number" },
          { header: "", align: "right", render: (r) => writable && (
            <div className="flex items-center gap-2 justify-end">
              <button onClick={() => openEdit(r)} className="text-stone-600 hover:text-emerald-800" data-testid={`customer-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></button>
              <button onClick={() => remove(r)} className="text-red-600 hover:text-red-800" data-testid={`customer-del-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          )},
        ]}
        rows={items}
      />
      <Modal open={open} onClose={() => setOpen(false)} title={editingId ? "Edit Customer" : "New Customer"} footer={<><Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn><Btn onClick={save} testid="customer-save">{editingId ? "Save Changes" : "Save"}</Btn></>}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name" required><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="customer-name" /></Field>
          <Field label="Code"><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></Field>
          <Field label="Country"><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></Field>
          <div className="col-span-2"><Field label="Billing Address"><Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Street, suburb, city, postal code" data-testid="customer-address" /></Field></div>
          <Field label="Contact Person"><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></Field>
          <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
          <Field label="VAT Number"><Input value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} /></Field>
        </div>
      </Modal>
    </>
  );
}

function SuppliersTab({ role }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const blank = { name: "", code: "", origin: "EIT", contact_person: "", email: "", phone: "", currency: "ZAR", vat_number: "" };
  const [form, setForm] = useState(blank);
  const writable = can(role, "master:write");
  const load = async () => setItems((await api.get("/suppliers")).data);
  useEffect(() => { load(); }, []);
  const openNew = () => { setEditingId(null); setForm(blank); setOpen(true); };
  const openEdit = (row) => { setEditingId(row.id); setForm({ ...blank, ...row }); setOpen(true); };
  const save = async () => {
    if (!form.name?.trim()) { toast.error("Name is required"); return; }
    try {
      if (editingId) { await api.patch(`/suppliers/${editingId}`, form); toast.success("Supplier updated"); }
      else { await api.post("/suppliers", form); toast.success("Supplier added"); }
      setOpen(false); setEditingId(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const remove = async (row) => {
    if (!window.confirm(`Delete supplier "${row.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/suppliers/${row.id}`); toast.success("Supplier deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  return (
    <>
      <Toolbar>
        {writable && <Btn onClick={openNew} testid="new-supplier-btn"><Plus className="h-4 w-4" /> New Supplier</Btn>}
        <div className="ml-auto text-xs text-stone-500">{items.length} suppliers</div>
      </Toolbar>
      <DataTable
        columns={[
          { header: "Name", key: "name" }, { header: "Code", key: "code" }, { header: "Origin", key: "origin" },
          { header: "Contact", key: "contact_person" }, { header: "Email", key: "email" }, { header: "VAT", key: "vat_number" },
          { header: "", align: "right", render: (r) => writable && (
            <div className="flex items-center gap-2 justify-end">
              <button onClick={() => openEdit(r)} className="text-stone-600 hover:text-emerald-800" data-testid={`supplier-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></button>
              <button onClick={() => remove(r)} className="text-red-600 hover:text-red-800" data-testid={`supplier-del-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          )},
        ]}
        rows={items}
      />
      <Modal open={open} onClose={() => setOpen(false)} title={editingId ? "Edit Supplier" : "New Supplier"} footer={<><Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn><Btn onClick={save} testid="supplier-save">{editingId ? "Save Changes" : "Save"}</Btn></>}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name" required><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supplier-name" /></Field>
          <Field label="Code"><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></Field>
          <Field label="Origin"><Select value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })}>{ORIGINS.map((o) => <option key={o}>{o}</option>)}</Select></Field>
          <Field label="Contact Person"><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></Field>
          <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
          <Field label="VAT Number"><Input value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} /></Field>
        </div>
      </Modal>
    </>
  );
}

function ProductsTab({ role }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const blank = { name: "", article_number: "", product_type: "Pole", default_unit: "pcs", description: "" };
  const [form, setForm] = useState(blank);
  const writable = can(role, "master:write");
  const load = async () => setItems((await api.get("/products")).data);
  useEffect(() => { load(); }, []);
  const openNew = () => { setEditingId(null); setForm(blank); setOpen(true); };
  const openEdit = (row) => { setEditingId(row.id); setForm({ ...blank, ...row }); setOpen(true); };
  const save = async () => {
    if (!form.name?.trim()) { toast.error("Name is required"); return; }
    try {
      if (editingId) { await api.patch(`/products/${editingId}`, form); toast.success("Product updated"); }
      else { await api.post("/products", form); toast.success("Product added"); }
      setOpen(false); setEditingId(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const remove = async (row) => {
    if (!window.confirm(`Delete product "${row.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/products/${row.id}`); toast.success("Product deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  return (
    <>
      <Toolbar>
        {writable && <Btn onClick={openNew} testid="new-product-btn"><Plus className="h-4 w-4" /> New Product</Btn>}
        <div className="ml-auto text-xs text-stone-500">{items.length} products</div>
      </Toolbar>
      <DataTable
        columns={[
          { header: "Name", key: "name" }, { header: "Article #", key: "article_number" }, { header: "Type", key: "product_type" },
          { header: "Unit", key: "default_unit" },
          { header: "", align: "right", render: (r) => writable && (
            <div className="flex items-center gap-2 justify-end">
              <button onClick={() => openEdit(r)} className="text-stone-600 hover:text-emerald-800" data-testid={`product-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></button>
              <button onClick={() => remove(r)} className="text-red-600 hover:text-red-800" data-testid={`product-del-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          )},
        ]}
        rows={items}
      />
      <Modal open={open} onClose={() => setOpen(false)} title={editingId ? "Edit Product" : "New Product"} footer={<><Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn><Btn onClick={save} testid="product-save">{editingId ? "Save Changes" : "Save"}</Btn></>}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name" required><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name" /></Field>
          <Field label="Article Number"><Input value={form.article_number} onChange={(e) => setForm({ ...form, article_number: e.target.value })} /></Field>
          <Field label="Product Type"><Select value={form.product_type} onChange={(e) => setForm({ ...form, product_type: e.target.value })}>
            <option>Pole</option><option>Log</option><option>Sleeper</option><option>Lumber</option></Select></Field>
          <Field label="Default Unit"><Input value={form.default_unit} onChange={(e) => setForm({ ...form, default_unit: e.target.value })} /></Field>
          <div className="col-span-2"><Field label="Description"><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field></div>
        </div>
      </Modal>
    </>
  );
}

function PaintColoursTab({ role }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const blank = { butt_mm: "", colour: "", swatch: "#888888", sort_order: 0, active: true, notes: "" };
  const [form, setForm] = useState(blank);

  const load = async () => setItems((await api.get("/paint-colours")).data);
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm({ ...row, notes: row.notes || "" }); setOpen(true); };

  const save = async () => {
    if (!form.colour || form.butt_mm === "" || form.butt_mm === null) {
      toast.error("Butt size and colour are required"); return;
    }
    const payload = {
      butt_mm: Number(form.butt_mm),
      colour: form.colour,
      swatch: form.swatch || "#888888",
      sort_order: Number(form.sort_order) || 0,
      active: !!form.active,
      notes: form.notes || null,
    };
    try {
      if (editing) {
        await api.patch(`/paint-colours/${editing.id}`, payload);
        toast.success("Paint colour updated");
      } else {
        await api.post("/paint-colours", payload);
        toast.success("Paint colour added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete colour ${row.colour} (${row.butt_mm} mm)?`)) return;
    try {
      await api.delete(`/paint-colours/${row.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <>
      <Toolbar>
        {can(role, "master:write") && <Btn onClick={openNew} testid="new-paint-btn"><Plus className="h-4 w-4" /> New Colour</Btn>}
        <div className="ml-auto text-xs text-stone-500">{items.length} colours · band assigned by nearest butt diameter</div>
      </Toolbar>
      <DataTable
        columns={[
          { header: "Sort", key: "sort_order", mono: true, align: "right" },
          { header: "Butt (mm)", mono: true, align: "right", render: (r) => r.butt_mm },
          { header: "Colour", render: (r) => (
            <span className="inline-flex items-center gap-2">
              <span className="inline-block h-4 w-4 rounded-full border border-stone-300" style={{ background: r.swatch }} />
              <span className="font-semibold uppercase tracking-wider text-xs">{r.colour}</span>
            </span>
          )},
          { header: "Swatch", mono: true, render: (r) => r.swatch },
          { header: "Active", render: (r) => r.active ? "Yes" : "No" },
          { header: "Notes", render: (r) => <span className="text-xs text-stone-600">{r.notes || "—"}</span> },
          { header: "", render: (r) => can(role, "master:write") && (
            <div className="flex items-center gap-2 justify-end">
              <button onClick={() => openEdit(r)} className="text-stone-600 hover:text-emerald-800" data-testid={`paint-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></button>
              <button onClick={() => remove(r)} className="text-red-600 hover:text-red-800" data-testid={`paint-del-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ), align: "right" },
        ]}
        rows={items}
      />
      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Paint Colour" : "New Paint Colour"} testid="paint-modal" footer={<><Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn><Btn onClick={save} testid="paint-save">Save</Btn></>}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Butt Size (mm)" required>
            <Input type="number" step="0.1" value={form.butt_mm}
                   onChange={(e) => setForm({ ...form, butt_mm: e.target.value })}
                   data-testid="paint-butt" />
          </Field>
          <Field label="Colour Name" required>
            <Input value={form.colour}
                   onChange={(e) => setForm({ ...form, colour: e.target.value })}
                   placeholder="e.g. Silver"
                   data-testid="paint-name" />
          </Field>
          <Field label="Swatch (hex)">
            <div className="flex items-center gap-2">
              <input type="color" value={form.swatch || "#888888"}
                     onChange={(e) => setForm({ ...form, swatch: e.target.value })}
                     className="h-9 w-12 rounded-sm border border-stone-300 cursor-pointer"
                     data-testid="paint-swatch-picker" />
              <Input value={form.swatch}
                     onChange={(e) => setForm({ ...form, swatch: e.target.value })}
                     placeholder="#c0c0c0" />
            </div>
          </Field>
          <Field label="Sort Order">
            <Input type="number" value={form.sort_order}
                   onChange={(e) => setForm({ ...form, sort_order: e.target.value })} />
          </Field>
          <Field label="Active">
            <Select value={form.active ? "yes" : "no"}
                    onChange={(e) => setForm({ ...form, active: e.target.value === "yes" })}>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </Select>
          </Field>
          <div className="col-span-2">
            <Field label="Notes">
              <Input value={form.notes}
                     onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </Field>
          </div>
        </div>
      </Modal>
    </>
  );
}

function ExportDestinationsTab({ role }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const blank = { code: "", details: "", active: true };
  const [form, setForm] = useState(blank);
  const writable = can(role, "master:write");
  const load = async () => setItems((await api.get("/export-destinations")).data);
  useEffect(() => { load(); }, []);
  const openNew = () => { setEditingId(null); setForm(blank); setOpen(true); };
  const openEdit = (row) => { setEditingId(row.id); setForm({ ...blank, ...row }); setOpen(true); };
  const save = async () => {
    if (!form.code?.trim()) { toast.error("Code is required"); return; }
    try {
      if (editingId) { await api.patch(`/export-destinations/${editingId}`, form); toast.success("Destination updated"); }
      else { await api.post("/export-destinations", form); toast.success("Destination added"); }
      setOpen(false); setEditingId(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const remove = async (row) => {
    if (!window.confirm(`Delete export destination "${row.code}"? This cannot be undone.`)) return;
    try { await api.delete(`/export-destinations/${row.id}`); toast.success("Destination deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  return (
    <>
      <Toolbar>
        {writable && <Btn onClick={openNew} testid="new-destination-btn"><Plus className="h-4 w-4" /> New Destination</Btn>}
        <div className="ml-auto text-xs text-stone-500">{items.length} destinations · shown as a dropdown on Orders; the details print as CONSIGNEE on the SI Manifest</div>
      </Toolbar>
      <DataTable
        columns={[
          { header: "Code", render: (r) => <span className="font-mono font-semibold" data-testid={`dest-code-${r.id}`}>{r.code}</span> },
          { header: "Consignee Details / Address", render: (r) => <span className="text-sm">{r.details || <span className="text-stone-400">—</span>}</span> },
          { header: "Active", render: (r) => r.active !== false
              ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">Active</span>
              : <span className="text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-500 border border-stone-200">Inactive</span> },
          { header: "", align: "right", render: (r) => writable && (
            <div className="flex items-center gap-2 justify-end">
              <button onClick={() => openEdit(r)} className="text-stone-600 hover:text-emerald-800" data-testid={`dest-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></button>
              <button onClick={() => remove(r)} className="text-red-600 hover:text-red-800" data-testid={`dest-del-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          )},
        ]}
        rows={items}
      />
      <Modal open={open} onClose={() => setOpen(false)} title={editingId ? "Edit Export Destination" : "New Export Destination"}
             footer={<><Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn><Btn onClick={save} testid="destination-save">{editingId ? "Save Changes" : "Save"}</Btn></>}>
        <div className="space-y-3">
          <Field label="Code" required><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g. CFR 7" data-testid="destination-code" /></Field>
          <Field label="Consignee Details / Address">
            <Textarea rows={3} value={form.details || ""} onChange={(e) => setForm({ ...form, details: e.target.value })}
                      placeholder="e.g. VAN SWAAY HARLINGEN: Industrieweg 10, 8861 VH Harlingen, the Netherlands" data-testid="destination-details" />
          </Field>
          <Field label="Active">
            <Select value={form.active !== false ? "yes" : "no"} onChange={(e) => setForm({ ...form, active: e.target.value === "yes" })} data-testid="destination-active">
              <option value="yes">Active — selectable on orders</option>
              <option value="no">Inactive — hidden from the order dropdown</option>
            </Select>
          </Field>
        </div>
      </Modal>
    </>
  );
}
